import { Component, Input, OnInit, ViewChild, AfterViewInit, ChangeDetectionStrategy } from '@angular/core';
import { finalize, Subscription } from 'rxjs';
import { ColumnDefinition } from '../../../model/column-definition';
import { DynamicExcelExportProvider } from '../../../model/dynamic-excel-export-provider';
import ListViewColumn from '../../../model/list-view-column';
import { MenuItemDefinition } from '../../../model/menu-item-definition';
import { ModelBase } from '../../../model/model-base';
import { CommandDispatcher } from '../../../service/command/command-dispatcher.service';
import { CommandFactoryService } from '../../../service/factory/command-factory.service';
import { ListViewColumnFactory } from '../../../service/factory/list-view-column-factory.service';
import { MessageService } from '../../../service/message.service';
import { NavigationService } from '../../../service/navigation.service';
import { ExcelPreferences, UserExcelPreferencesService } from '../../../service/user-excel-preferences.service';
import { AdditionalExcelSheetItem, SidebarItem } from '../../../model/selectable-items';
import { NativeWindowService } from '../../../service/native-window.service';
import { ExcelExportService } from '../../../service/excel-export.service';
import { ExcelManagementSidebarComponent } from '../excel-management-sidebar/excel-management-sidebar.component';
import { LanguageService } from '../../../service/language.service';

@Component({
    selector: 'app-excel-list',
    templateUrl: './excel-list.component.html',
    styleUrls: ['./excel-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ExcelListComponent<TSource extends { id: string } | ModelBase = ModelBase> implements OnInit, AfterViewInit {
  constructor(
    protected columnFactory: ListViewColumnFactory,
    protected languageService: LanguageService,
    protected commandDispatcher: CommandDispatcher,
    protected commandFactory: CommandFactoryService,
    protected messageService: MessageService,
    protected _navigation: NavigationService,
    private _nativeWindow: NativeWindowService,
    private _excelExportService: ExcelExportService,
    private readonly excelPrefsService: UserExcelPreferencesService
  ) {
    this.subscription = new Subscription();
    this.subscription.add(
      languageService.onLangChange.subscribe(e => {
        this.columns.forEach(col => (col.locale = e.lang));
      })
    );
  }

  @Input() columnDefinition: ColumnDefinition<TSource>[];
  @Input() columns: ListViewColumn[];
  @Input() contextMenuDefinition: MenuItemDefinition[];
  @Input() totalCount: number;
  @Input() excelContext?: string;
  @Input() fileName: string;
  @Input() filter: any;
  @Input() isMobile: boolean = false;
  @Input() selectedListViewColumns: ListViewColumn[];
  @Input() selectedIds: string[];
  @Input() deSelectedIds: string[];
  @Input() affectedItemsCount: number;
  @Input() allSelected: boolean;
  @Input() totalResults: number;
  @Input() excelExportProvider?: DynamicExcelExportProvider;
  @Input() additionalSheetItems: AdditionalExcelSheetItem[] = [];

  @ViewChild(ExcelManagementSidebarComponent)
  private excelSidebar!: ExcelManagementSidebarComponent;

  protected subscription: Subscription;

  private _excelPreferences: ExcelPreferences;
  showExcelColumnSidebar = false;
  isLoading: boolean = false;
  excelColumnItems: SidebarItem[] = [];
  hasExcelPreferences: boolean = false;
  ngOnInit() {}

  ngAfterViewInit(): void {
    const ctrl = this.excelSidebar?.form?.get('radioButtons');
    if (!ctrl) return;

    if (ctrl.value) {
      this.updateSidebarColumns(ctrl.value);
    }
    ctrl.valueChanges.subscribe((mode: 'exportListViewColumns' | 'exportPreferencesColumns') => {
      this.updateSidebarColumns(mode);
    });
  }

  public loadExcelPreferences(): void {
    if (!this.excelContext) {
      return;
    }
    if (!this.columns && this.columnDefinition) {
      this.columns = this.columnFactory.create(this.columnDefinition);
    }
    this.isLoading = true;
    this.excelPrefsService
      .getPreferences(this.excelContext)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe(prefs => {
        if (prefs?.columns?.length > 0) {
          this.hasExcelPreferences = true;
          this._excelPreferences = prefs ?? {
            context: this.excelContext,
            columns: prefs.columns,
          };
        } else {
          this._excelPreferences = {
            context: this.excelContext,
            columns: this.selectedListViewColumns?.map(col => col.binding),
          };
        }

        const model = {
          context: this.excelContext,
          columns: this.selectedListViewColumns?.map(col => col.binding),
        };

        const initModelToUse = this.excelSidebar?.isListViewModeSelected || !this.hasExcelPreferences ? model : this._excelPreferences;

        this.excelColumnItems = this.columnDefinition.map((col, index) => ({
          caption: col.caption,
          binding: col.binding,
          selected: initModelToUse.columns?.includes(col.binding),
          id: index,
          name: null,
        }));

        if (initModelToUse.columns?.length > 0) {
          this.excelColumnItems.sort((a, b) => {
            const indexA = initModelToUse.columns.indexOf(a.binding);
            const indexB = initModelToUse.columns.indexOf(b.binding);

            if (indexA === -1) return 1;
            if (indexB === -1) return -1;

            return indexA - indexB;
          });
        }
        this.updateAdditionalSheetSelections(
          initModelToUse.columns ?? [],
          this.excelSidebar?.isListViewModeSelected !== true && this.hasExcelPreferences
        );
        this.showExcelColumnSidebar = true;
      });
  }

  onExcelColumnsSave(selection: { columns: SidebarItem[]; additionalSheets: AdditionalExcelSheetItem[] }) {
    if (!this.excelContext) return;

    const selectedColumns = selection.columns.filter(item => item.selected).map(item => item.binding);
    if (selectedColumns.length === 0) return;

    const selectedAdditionalSheets = this.excelSidebar?.isListViewModeSelected
      ? []
      : selection.additionalSheets.filter(item => item.selected).map(item => item.binding);
    const preferenceColumns = [...selectedColumns, ...selectedAdditionalSheets];

    if (!this.hasExcelPreferences || !this.excelSidebar?.isListViewModeSelected) {
      const preference: ExcelPreferences = { context: this.excelContext, columns: preferenceColumns };

      this.isLoading = true;
      this.excelPrefsService
        .savePreferences(preference)
        .pipe(
          finalize(() => {
            this.isLoading = false;
            this.showExcelColumnSidebar = false;
          })
        )
        .subscribe(() => {
          this._excelPreferences = preference;
          if (this.columns) {
            this.columns.forEach(col => (col.selected = preference.columns.includes(col.binding)));
            this.columns.sort((a, b) => {
              const indexA = preference.columns.indexOf(a.binding);
              const indexB = preference.columns.indexOf(b.binding);
              if (indexA === -1) return 1;
              if (indexB === -1) return -1;
              return indexA - indexB;
            });
          }

          const runListViewExport = this.excelSidebar?.isListViewModeSelected === true;
          if (runListViewExport) {
            this.exportDynamicExcel();
          } else {
            this.exportDynamicExcelByPreferences(preferenceColumns);
          }
        });
    } else if (this.excelSidebar?.isListViewModeSelected) {
      this.exportDynamicExcel();
    }
  }

  exportDynamicExcel(): void {
    const selectedBindings = this.selectedListViewColumns?.map(col => col.binding) ?? [];
    this.performExport(selectedBindings);
  }

  exportDynamicExcelByPreferences(selectedBindings: string[]): void {
    this.performExport(selectedBindings);
  }

  private performExport(selectedBindings: string[]): void {
    this.safeUnsubscribe();

    const { included, excluded, count } = this.buildSelectionArgs();
    const exportProvider = this.excelExportProvider?.getDynamicExcelExport ? this.excelExportProvider : this._excelExportService;

    this.subscription = exportProvider
      .getDynamicExcelExport(this.filter, this.excelContext, this.fileName, selectedBindings, included, excluded, count)
      .subscribe(file => this._nativeWindow.save(file));
  }

  private buildSelectionArgs(): { included?: string[]; excluded?: string[]; count?: number } {
    // Read the checkbox that belongs to the currently selected radio
    const useSelection = this.excelSidebar?.isApplySelectionChecked === true;
    return {
      included: useSelection ? this.selectedIds : undefined,
      excluded: useSelection ? this.deSelectedIds : undefined,
      count: useSelection ? this.affectedItemsCount : undefined,
    };
  }

  private safeUnsubscribe(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    this.subscription = null;
  }

  openSideBar(): void {
    this.loadExcelPreferences();
  }

  get showCheckbox(): boolean {
    if (this.deSelectedIds && this.deSelectedIds.length > 0) return true;
    if (this.selectedIds && this.selectedIds.length > 0 && !this.allSelected && this.totalResults !== this.selectedIds.length) return true;
    return false;
  }

  sideBarCancel(): void {
    this.showExcelColumnSidebar = false;
  }

  private updateSidebarColumns(mode: 'exportListViewColumns' | 'exportPreferencesColumns'): void {
    const activeBindings =
      mode === 'exportListViewColumns' ? this.selectedListViewColumns?.map(c => c.binding) ?? [] : this._excelPreferences?.columns ?? [];

    this.excelColumnItems = this.columnDefinition.map((col, index) => ({
      caption: col.caption,
      binding: col.binding,
      selected: activeBindings.includes(col.binding),
      id: index,
      name: null,
    }));

    this.updateAdditionalSheetSelections(activeBindings, mode === 'exportPreferencesColumns');

    if (activeBindings.length > 0) {
      this.excelColumnItems.sort((a, b) => {
        const ia = activeBindings.indexOf(a.binding);
        const ib = activeBindings.indexOf(b.binding);
        if (ia === -1) return 1;
        if (ib === -1) return -1;
        return ia - ib;
      });
    }
  }

  private updateAdditionalSheetSelections(activeBindings: string[], enabled: boolean): void {
    this.additionalSheetItems = this.additionalSheetItems.map(item => ({
      ...item,
      selected: enabled && activeBindings.includes(item.binding),
    }));
  }
}
